import { useMutation } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useInvalidateBanking } from '@/components/banking/line-editor';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import {
  useBankAccounts,
  useReconciliations,
  type Reconciliation,
  type ReconcileWorkspace,
} from '@/lib/banking';
import { MEMO_MAX, reconcileStartErrors } from '@/lib/banking-forms';
import { useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { ltr } from '@/lib/arabic';
import { useT } from '@/lib/i18n';
import { evaluateAmount } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useAccountNames } from '@/lib/accounts';

export const Route = createFileRoute('/banking/reconcile/')({
  component: ReconcilePage,
  validateSearch: (s: Record<string, unknown>): { accountId?: string } =>
    typeof s['accountId'] === 'string' ? { accountId: s['accountId'] } : {},
});

const STATUS: Record<
  Reconciliation['status'],
  {
    label: 'statusInProgress' | 'statusReconciled' | 'statusUndone';
    variant: 'secondary' | 'outline' | 'default';
  }
> = {
  in_progress: { label: 'statusInProgress', variant: 'outline' },
  finished: { label: 'statusReconciled', variant: 'secondary' },
  undone: { label: 'statusUndone', variant: 'outline' },
};

/** Screen B4 (document 06): Reconcile, step 1 and history. */
function ReconcilePage() {
  const orgId = useOrgId();
  const t = useT();
  const names = useAccountNames();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const banks = useBankAccounts();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const invalidate = useInvalidateBanking();
  const accountId = search.accountId ?? banks.data?.[0]?.accountId ?? null;
  const bank = banks.data?.find((b) => b.accountId === accountId) ?? null;
  const history = useReconciliations(accountId);
  const [endDate, setEndDate] = useState('');
  const [endBalance, setEndBalance] = useState('');
  const [negative, setNegative] = useState(false);
  /** AmountInput shows a zero as blank; remember that 0 was typed (a card paid off, a closed account). */
  const [zeroTyped, setZeroTyped] = useState(false);
  const [tried, setTried] = useState(false);
  const [undoing, setUndoing] = useState<Reconciliation | null>(null);
  const [deleting, setDeleting] = useState<Reconciliation | null>(null);
  useEffect(() => {
    setEndDate('');
    setEndBalance('');
    setNegative(false);
    setZeroTyped(false);
    setTried(false);
  }, [accountId]);

  const start = useMutation({
    mutationFn: () =>
      api<ReconcileWorkspace>(`/orgs/${orgId}/banking/reconciliations`, {
        method: 'POST',
        body: JSON.stringify({
          accountId,
          statementDate: endDate,
          statementBalance: `${negative ? '-' : ''}${endBalance || '0'}`,
        }),
      }),
    onSuccess: async (w) => {
      await invalidate();
      void navigate({
        to: '/banking/reconcile/$reconciliationId',
        params: { reconciliationId: w.reconciliation.id },
      });
    },
  });
  const remove = useMutation({
    mutationFn: (r: Reconciliation) =>
      api(`/orgs/${orgId}/banking/reconciliations/${r.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidate();
      setDeleting(null);
      toast(t('purchasePages.reconcile.deleted'));
    },
  });

  const open = history.data?.find((r) => r.status === 'in_progress') ?? null;
  const startErrors = reconcileStartErrors({
    endBalance,
    zeroTyped,
    endDate,
    lastReconciledDate: bank?.lastReconciledDate ?? null,
  });
  const card = bank?.accountType === 'credit_card';

  return (
    <>
      <PageHeader
        title={t('purchasePages.reconcile.title')}
        description={t('purchasePages.reconcile.description')}
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} />
      ) : banks.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : banks.isError ? (
        <EmptyState title={t('purchasePages.reconcile.loadFailed')} hint={banks.error.message} />
      ) : !bank ? (
        <EmptyState title={t('purchasePages.reconcile.noBanks')} />
      ) : (
        <>
          <div className="mb-6 max-w-3xl rounded-lg border bg-card p-5">
            <h2 className="mb-3 text-base font-semibold">
              {t('purchasePages.reconcile.whichAccount')}
            </h2>
            <ErrorBanner>{start.error?.message ?? null}</ErrorBanner>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t('purchasePages.reconcile.account')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={bank.accountId}
                    onChange={(e) => void navigate({ search: { accountId: e.target.value } })}
                  >
                    {banks.data.map((b) => (
                      <option key={b.accountId} value={b.accountId}>
                        {b.code} · {names.name(b.accountId, b.name)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <div className="flex flex-col justify-end text-sm">
                <div className="text-muted-foreground">
                  {bank.lastReconciledDate
                    ? t('purchasePages.reconcile.lastEnding', {
                        date: displayDate(bank.lastReconciledDate),
                      })
                    : t('purchasePages.reconcile.neverReconciled')}
                </div>
              </div>
            </div>
            {open ? (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
                <span>
                  {t('purchasePages.reconcile.savedForLater', {
                    date: displayDate(open.statementDate),
                    amount: ltr(`${open.currency} ${formatAmount(open.statementBalance)}`),
                  })}
                </span>
                <Button size="sm" asChild className="ms-auto">
                  <Link
                    to="/banking/reconcile/$reconciliationId"
                    params={{ reconciliationId: open.id }}
                  >
                    {t('purchasePages.reconcile.resumeReconciling')}
                  </Link>
                </Button>
              </div>
            ) : canEdit ? (
              <>
                <h3 className="mb-2 mt-5 text-sm font-semibold">
                  {t('purchasePages.reconcile.addInfo')}
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Field
                    label={t('purchasePages.reconcile.beginningBalance')}
                    hint={t('purchasePages.reconcile.beginningHint')}
                  >
                    {(id) => (
                      <input
                        id={id}
                        readOnly
                        className="tnum h-9 rounded-md border border-input bg-muted px-3 text-end text-sm"
                        value={formatAmount(bank.lastReconciledBalance ?? '0')}
                      />
                    )}
                  </Field>
                  <Field
                    label={t('purchasePages.reconcile.endingBalanceRequired')}
                    hint={card ? t('purchasePages.reconcile.endingCardHint') : undefined}
                    error={tried ? startErrors['endBalance'] : undefined}
                  >
                    {(id) => (
                      <div className="flex items-center gap-2">
                        <AmountInput
                          id={id}
                          className="h-9 w-full rounded-md border border-input bg-card px-3 text-end text-sm"
                          value={endBalance}
                          invalid={tried && Boolean(startErrors['endBalance'])}
                          onValueChange={setEndBalance}
                          onBlur={(e) => setZeroTyped(evaluateAmount(e.currentTarget.value) === 0n)}
                        />
                      </div>
                    )}
                  </Field>
                  <Field
                    label={t('purchasePages.reconcile.endingDateRequired')}
                    error={tried ? startErrors['endDate'] : undefined}
                  >
                    {(id) => (
                      <DateInput
                        id={id}
                        value={endDate}
                        onChange={setEndDate}
                        invalid={tried && Boolean(startErrors['endDate'])}
                      />
                    )}
                  </Field>
                </div>
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    className="size-3.5 accent-primary"
                    checked={negative}
                    onChange={(e) => setNegative(e.target.checked)}
                  />
                  {card
                    ? t('purchasePages.reconcile.negativeCard')
                    : t('purchasePages.reconcile.negativeBank')}
                </label>
                <div className="mt-4">
                  <Button
                    disabled={start.isPending}
                    onClick={() => {
                      setTried(true);
                      if (!Object.keys(startErrors).length) start.mutate();
                    }}
                  >
                    {start.isPending
                      ? t('purchasePages.reconcile.starting')
                      : t('purchasePages.reconcile.start')}
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          <h2 className="mb-2 text-base font-semibold">
            {t('purchasePages.reconcile.historyFor', {
              name: names.name(bank.accountId, bank.name),
            })}
          </h2>
          {history.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : !history.data?.length ? (
            <EmptyState title={t('purchasePages.reconcile.noHistory')} />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">
                      {t('purchasePages.reconcile.colEndingDate')}
                    </TableHead>
                    <TableHead>{t('purchasePages.reconcile.colStatus')}</TableHead>
                    <TableHead className="text-end">
                      {t('purchasePages.reconcile.colEndingBalance')}
                    </TableHead>
                    <TableHead className="text-end">
                      {t('purchasePages.reconcile.colBeginningBalance')}
                    </TableHead>
                    <TableHead>{t('purchasePages.reconcile.colReconciledOn')}</TableHead>
                    <TableHead className="w-[220px] text-end">
                      {t('purchasePages.reconcile.colAction')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.data.map((r) => (
                    <TableRow key={r.id} data-testid="reconciliation-row">
                      <TableCell className="tnum">{displayDate(r.statementDate)}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS[r.status].variant}>
                          {t(`purchasePages.reconcile.${STATUS[r.status].label}`)}
                        </Badge>
                        {r.status === 'undone' && r.undoReason ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {r.undoneByName ? (
                              <>
                                <bdi>{r.undoneByName}</bdi>:{' '}
                              </>
                            ) : (
                              ''
                            )}
                            {r.undoReason}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="tnum text-end">
                        {formatAmount(r.statementBalance)}
                      </TableCell>
                      <TableCell className="tnum text-end">
                        {formatAmount(r.beginningBalance)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.finishedAt
                          ? `${displayDate(r.finishedAt.slice(0, 10))}${r.finishedByName ? ` · ${r.finishedByName}` : ''}`
                          : ''}
                      </TableCell>
                      <TableCell className="text-end">
                        {r.status === 'in_progress' ? (
                          <>
                            <Button size="sm" variant="link" className="px-2" asChild>
                              <Link
                                to="/banking/reconcile/$reconciliationId"
                                params={{ reconciliationId: r.id }}
                              >
                                {t('purchasePages.reconcile.resume')}
                              </Link>
                            </Button>
                            {canEdit ? (
                              <Button
                                size="sm"
                                variant="link"
                                className="px-2 text-destructive"
                                onClick={() => setDeleting(r)}
                              >
                                {t('purchasePages.reconcile.delete')}
                              </Button>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="link" className="px-2" asChild>
                              <Link
                                to="/banking/reconcile/$reconciliationId/report"
                                params={{ reconciliationId: r.id }}
                              >
                                {t('purchasePages.reconcile.viewReport')}
                              </Link>
                            </Button>
                            {canEdit && r.status === 'finished' && r.isLatest ? (
                              <Button
                                size="sm"
                                variant="link"
                                className="px-2 text-destructive"
                                onClick={() => setUndoing(r)}
                              >
                                {t('purchasePages.reconcile.undo')}
                              </Button>
                            ) : null}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
      <UndoDialog rec={undoing} onClose={() => setUndoing(null)} />
      <Dialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('purchasePages.reconcile.deleteTitle')}</DialogTitle>
            <DialogDescription>{t('purchasePages.reconcile.deleteBody')}</DialogDescription>
          </DialogHeader>
          {remove.error ? <p className="text-sm text-destructive">{remove.error.message}</p> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {t('purchasePages.reconcile.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Undo the last reconciliation (accountants; ConfirmDialog with a typed reason, audited). */
function UndoDialog({ rec, onClose }: { rec: Reconciliation | null; onClose: () => void }) {
  const orgId = useOrgId();
  const invalidate = useInvalidateBanking();
  const t = useT();
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [rec]);
  const undo = useMutation({
    mutationFn: () =>
      api<Reconciliation>(`/orgs/${orgId}/banking/reconciliations/${rec!.id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      }),
    onSuccess: async () => {
      await invalidate();
      toast(t('purchasePages.reconcile.undoneToast'));
      onClose();
    },
  });
  return (
    <Dialog
      open={Boolean(rec)}
      onOpenChange={(o) => {
        if (!o) {
          undo.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('purchasePages.reconcile.undoTitle', {
              date: rec ? displayDate(rec.statementDate) : '',
            })}
          </DialogTitle>
          <DialogDescription>{t('purchasePages.reconcile.undoBody')}</DialogDescription>
        </DialogHeader>
        <Field
          label={t('purchasePages.reconcile.reason')}
          hint={t('purchasePages.reconcile.reasonHint')}
        >
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              maxLength={MEMO_MAX}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        {undo.error ? (
          <p role="alert" className="text-sm text-destructive">
            {undo.error instanceof ApiError ? undo.error.message : String(undo.error)}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3 || undo.isPending}
            onClick={() => undo.mutate()}
          >
            {undo.isPending
              ? t('purchasePages.reconcile.undoing')
              : t('purchasePages.reconcile.undoReconciliation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
